import { action, computed, observable } from 'mobx';

import { AppManager, Variables, routerStore } from 'core/index';

import { ChannelConnManager } from 'apps/channel/main/connManager';
import { ProfileConnManager } from 'apps/profile/main/connManager';
import { ProjectConnManager } from 'apps/project/connManager';
import { ThreadConnManager } from 'apps/forum/main/appManager';
import { InitiativeConnManager } from 'apps/initiative/connManager';
import { StoryConnManager } from 'apps/story/main/appManager';
import { CollectionConnManager } from 'apps/collection/main/connManager';

import {
  SearchQuery,
  ProfilesSearchQuery,
  ProjectsSearchQuery,
  ChannelsSearchQuery,
  ThreadsSearchQuery,
  InitiativesSearchQuery,
  StoriesSearchQuery,
  CollectionsSearchQuery,
} from 'apps/search/gql';

import i18next from 'i18next';

export class SearchAppManager extends AppManager {
  static registryName = 'SearchAppManager';
  @observable resultFilter = 'none';
  @observable lastSearch = '';
  constructor(parent, init = {}, options) {
    super(parent, init, options);

    this.variables = new Variables({
      q: () => this.searchQuery,
      // channels
      channelsCursor: () => this.channelsConn.endCursor,
      channelsCount: () => this.channelsConn.variables.batchSize,
      channelsSortBy: () => this.channelsConn.sortBy,
      // profiles
      profilesCursor: () => this.profilesConn.endCursor,
      profilesCount: () => this.profilesConn.variables.batchSize,
      profilesSortBy: () => this.profilesConn.sortBy,
      // projects
      projectsCursor: () => this.projectsConn.endCursor,
      projectsCount: () => this.projectsConn.variables.batchSize,
      projectsSortBy: () => this.projectsConn.sortBy,
      // posts
      threadsCursor: () => this.threadsConn.endCursor,
      threadsSortBy: () => this.threadsConn.sortBy,
      threadsBatchSize: () => this.threadsConn.batchSize,
      // initiatives
      initiativesCursor: () => this.initiativesConn.endCursor,
      initiativesCount: () => this.initiativesConn.variables.batchSize,
      initiativesSortBy: () => this.initiativesConn.sortBy,
      // stories
      storiesCursor: () => this.storiesConn.endCursor,
      storiesCount: () => this.storiesConn.variables.batchSize,
      storiesSortBy: () => this.storiesConn.sortBy,
      // collections
      collectionsCursor: () => this.collectionsConn.endCursor,
      collectionsCount: () => this.collectionsConn.variables.batchSize,
      collectionsSortBy: () => this.collectionsConn.sortBy,
    });

    this.channelsConn = new ChannelConnManager(this, {
      initialBatchSize: 6,
      sortBy: '',
      sortOptions: [],
      loadMoreQuery: ChannelsSearchQuery,
    });
    this.profilesConn = new ProfileConnManager(this, {
      sortBy: '',
      sortOptions: [],
      loadMoreQuery: ProfilesSearchQuery,
    });
    this.projectsConn = new ProjectConnManager(this, {
      initialBatchSize: 6,
      sortBy: '',
      sortOptions: [],
      loadMoreQuery: ProjectsSearchQuery,
    });
    this.threadsConn = new ThreadConnManager(this, {
      sortBy: '',
      sortOptions: [],
      loadMoreQuery: ThreadsSearchQuery,
    });
    this.initiativesConn = new InitiativeConnManager(this, {
      sortBy: '',
      sortOptions: [],
      loadMoreQuery: InitiativesSearchQuery,
    });
    this.storiesConn = new StoryConnManager(this, {
      sortBy: 'recent',
      sortOptions: [],
      loadMoreQuery: StoriesSearchQuery,
    });
    this.collectionsConn = new CollectionConnManager(this, {
      sortBy: '',
      sortOptions: [],
      loadMoreQuery: CollectionsSearchQuery,
    });

    this.variables.onChange(['q'], this.NewSearch);
  }

  @computed get loading() {
    return this.queries.get('search')?.loading;
  }

  @computed get searchQuery() {
    if (!routerStore.params) {
      return '';
    }
    const q = routerStore.params.get('q');
    return q === undefined ? this.lastSearch : q;
  }

  @computed get matchedTypes() {
    const types = [];
    if (this.channelsConn.arrayApp.length > 0) {
      types.push([
        'channels',
        i18next.t('Topics ({{topics}})', {
          topics: this.channelsConn.totalCount,
        }),
      ]);
    }
    if (this.initiativesConn.arrayApp.length > 0) {
      types.push([
        'initiatives',
        i18next.t('Organisations ({{initiatives}})', {
          initiatives: this.initiativesConn.totalCount,
        }),
      ]);
    }
    if (this.profilesConn.arrayApp.length > 0) {
      types.push([
        'profiles',
        i18next.t('Profiles ({{profiles}})', {
          profiles: this.profilesConn.totalCount,
        }),
      ]);
    }
    if (this.projectsConn.arrayApp.length > 0) {
      types.push([
        'projects',
        i18next.t('Projects ({{projects}})', {
          projects: this.projectsConn.totalCount,
        }),
      ]);
    }
    if (this.threadsConn.arrayApp.length > 0) {
      types.push([
        'threads',
        i18next.t('Threads ({{posts}})', {
          posts: this.threadsConn.totalCount,
        }),
      ]);
    }
    if (this.storiesConn.arrayApp.length > 0) {
      types.push([
        'stories',
        i18next.t('Stories ({{stories}})', {
          stories: this.storiesConn.totalCount,
        }),
      ]);
    }
    if (this.collectionsConn.arrayApp.length > 0) {
      types.push([
        'collections',
        i18next.t('Collections ({{collections}})', {
          collections: this.collectionsConn.totalCount,
        }),
      ]);
    }
    return types;
  }

  @computed get matchedAndFilteredTypes() {
    if (this.resultFilter === 'none') {
      return this.matchedTypes;
    }
    return this.matchedTypes.filter(type => type[0] === this.resultFilter)[0];
  }

  @computed get totalMatchCount() {
    return (
      this.channelsConn.totalCount +
      this.profilesConn.totalCount +
      this.projectsConn.totalCount +
      this.threadsConn.totalCount +
      this.initiativesConn.totalCount +
      this.storiesConn.totalCount +
      this.collectionsConn.totalCount
    );
  }

  @action NewSearch = () => {
    if (!this.variables.q) return;
    this.lastSearch = this.variables.q;
    this.channelsConn.NewSlug();
    this.profilesConn.NewSlug();
    this.projectsConn.NewUsername();
    this.threadsConn.NewVariables();
    this.initiativesConn.NewUsername();
    this.storiesConn.NewUsername();
    this.collectionsConn.NewUsername();
    this.NewSearchQuery();
  };

  @action NewSearchQuery = () => {
    const q = new SearchQuery(this, {
      queryKey: 'search',
      onSysErr: this.onInitiativeError,
    });
    q.process(this.channelsConn.process, this.ChannelsUpdate, {
      subPath: 'channels',
    });
    q.process(this.profilesConn.process, this.ProfilesUpdate, {
      subPath: 'profiles',
    });
    q.process(this.projectsConn.process, this.ProjectsUpdate, {
      subPath: 'projects',
    });
    q.process(this.threadsConn.process, this.PostsUpdate, {
      subPath: 'threads',
    });
    q.process(this.initiativesConn.process, this.InitiativesUpdate, {
      subPath: 'initiatives',
    });
    q.process(this.storiesConn.process, this.StoriesUpdate, {
      subPath: 'stories',
    });
    q.process(this.collectionsConn.process, this.CollectionsUpdate, {
      subPath: 'collections',
    });
    // etc...
    q.query();
  };

  @action ChannelsUpdate = (channels, query) => {
    this.channelsConn.pushResultIfFirst(channels, query);
  };

  @action ProfilesUpdate = (profiles, query) => {
    this.profilesConn.pushResultIfFirst(profiles, query);
  };

  @action ProjectsUpdate = (projects, query) => {
    this.projectsConn.pushResultIfFirst(projects, query);
  };

  @action PostsUpdate = (posts, query) => {
    this.threadsConn.pushResultIfFirst(posts, query);
  };

  @action InitiativesUpdate = (initiatives, query) => {
    this.initiativesConn.pushResultIfFirst(initiatives, query);
  };

  @action StoriesUpdate = (stories, query) => {
    this.storiesConn.pushResultIfFirst(stories, query);
  };

  @action CollectionsUpdate = (collections, query) => {
    this.collectionsConn.pushResultIfFirst(collections, query);
  };

  @action SetResultFilter = filter => {
    this.resultFilter = filter;
  };
}

export const searchManager = new SearchAppManager(
  null,
  {},
  { autoDispose: false },
);
